import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import { DialogBase } from "@modules/slides-library/shared";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { type KeyboardEventHandler, useCallback, useState } from "react";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { handleAsyncOperation } from "@/shared";
import { useUpdateSlidesFolderMutation } from "@/shared/services";
import type { TSlidesFolder } from "@/shared/types";

const DIALOG_ID = "rename-folder-dialog";

const FolderRenameDialog = ({ folder }: { folder: TSlidesFolder }) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState(folder.title);
  const { mutateAsync: updateFolder, isPending: isLoading } =
    useUpdateSlidesFolderMutation();

  const handleClose = useCallback(() => {
    dispatch(closeDialog(DIALOG_ID));
  }, [dispatch]);

  const handleSubmit = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName || isLoading) return;

    await handleAsyncOperation({
      operation: async () => {
        await updateFolder({
          id: folder._id,
          title: trimmedName,
        });
        handleClose();
      },
      onError: () => toast.error("Failed to rename folder"),
    });
  }, [updateFolder, folder._id, handleClose, isLoading, name]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.key === "Enter") handleSubmit();
    },
    [handleSubmit],
  );

  return (
    <DialogBase>
      <DialogBase.Header title="Rename folder" onClose={handleClose} />
      <DialogBase.Content>
        <span className="tw-mb-1 tw-block tw-font-sans tw-text-[13px] tw-font-semibold">
          Name
        </span>
        <Input
          placeholder="Add your folder name here..."
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </DialogBase.Content>
      <DialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!name?.trim() || name === folder.title}
          className={cn(isLoading && "tw-cursor-not-allowed")}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Saving...
            </>
          ) : (
            "Save changes"
          )}
        </Button>
      </DialogBase.Footer>
    </DialogBase>
  );
};

FolderRenameDialog.id = DIALOG_ID;
export { FolderRenameDialog };
